import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/api/client";

/**
 * The candidate application form.
 *
 * Step one of a sitting. Replaces the dynamic intro questions, which
 * still exist and still work but are no longer wired to anything — see
 * CLAUDE.md, "Inert code kept on purpose".
 */

export interface CandidateApplication {
  id: string;
  candidate_user_id: string;

  // 01 About you
  full_name: string | null;
  email: string | null;
  phone: string | null;
  country_of_training: string | null;

  // 02 Registration & eligibility
  ahpra_status: string | null;
  ranzcr_pathway: string | null;
  defined_scope_area: string | null;
  psv_status: string | null;
  english_requirement: string | null;
  english_scores: string | null;
  work_rights: string | null;
  registration_conditions: string | null;

  // 03 Qualifications & experience
  primary_qualification: string | null;
  years_since_qualification: number | null;
  current_post: string | null;
  last_independent_report: string | null;
  subspecialty_interests: string | null;
  unsupervised_experience: string | null;

  // 04 Systems & dictation — keys match the panel template's stage 07.
  confidence: Record<string, number>;
  vr_system: string | null;
  pacs_ris: string | null;
  reporting_method: string | null;

  // 05 Reporting capacity — keys match the panel template's stage 07.
  volumes: Record<string, number | null>;
  throughput_context: string | null;

  // 06 Procedures
  procedures: string[];
  procedure_log: string | null;

  // 07 Your plan
  why_role: string | null;
  first_12_months: string | null;
  path_to_fellowship: string | null;
  years_three_to_five: string | null;
  beyond_five_years: string | null;
  support_needed: string | null;
  notice_period: string | null;
  able_to_relocate: string | null;

  // 08 Situational judgement
  sj_retrospective_miss: string | null;
  sj_pressured_report: string | null;
  sj_junior_disagrees: string | null;
  sj_long_shift: string | null;
  sj_was_wrong: string | null;

  // 09 Return of service — bond_stance and bond_risk are what the panel
  // scores as stage 04's "bond" and "candour".
  bond_stance: string | null;
  bond_comment: string | null;
  bond_risk: string | null;
  bond_acknowledged: boolean;

  // The declaration: three separate consents, not one flag.
  declare_true: boolean;
  declare_verification: boolean;
  declare_live_assessment: boolean;

  /** Set by the SERVER, and only when all four ticks are true. */
  declaration_accepted: boolean;
  /**
   * Set when sections 01–06 were carried from a previous sitting, and
   * equal to when they were last affirmed. Null on a first application.
   */
  carried_from: string | null;
  /** The candidate has confirmed the carried sections are still true. */
  carried_confirmed: boolean;
  submitted_at: string | null;
  updated_at: string | null;
}

/** Every field is optional: the form saves a section at a time. */
export type ApplicationPatch = Partial<
  Omit<
    CandidateApplication,
    | "id"
    | "candidate_user_id"
    | "submitted_at"
    | "updated_at"
    | "declaration_accepted"
    | "carried_from"
  >
> & { submitted?: boolean };

export const APPLICATION_KEY = ["application", "me"] as const;

export function useMyApplication(
  /** Off when the form is showing somebody ELSE's answers: this route
   *  reads the signed-in person's own row, so a reviewer calling it
   *  gets a 403 -- correctly. */
  enabled = true,
): UseQueryResult<CandidateApplication> {
  return useQuery({
    enabled,
    queryKey: APPLICATION_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<CandidateApplication>(
        "/tests/application/me",
      );
      return data;
    },
    // The row is created on first touch, so a refetch after the tab
    // regains focus must not stamp a half-typed section back over the
    // server's copy. The form owns its own draft state; this query is
    // the seed, not the source of truth while typing.
    refetchOnWindowFocus: false,
  });
}

export function useSaveApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApplicationPatch) => {
      const { data } = await apiClient.put<CandidateApplication>(
        "/tests/application/me",
        body,
      );
      return data;
    },
    onSuccess: (data) => qc.setQueryData(APPLICATION_KEY, data),
  });
}

/** Staff read, for the panel console. 404 when they have not started. */
export function useCandidateApplication(
  candidateId: string | null,
): UseQueryResult<CandidateApplication> {
  return useQuery({
    queryKey: ["application", candidateId ?? ""],
    enabled: !!candidateId,
    retry: false, // a 404 here is an answer, not a failure to retry
    queryFn: async () => {
      const { data } = await apiClient.get<CandidateApplication>(
        `/tests/application/${candidateId}`,
      );
      return data;
    },
  });
}

/**
 * The application AS IT STOOD for one sitting.
 *
 * Prefer this over `useCandidateApplication` wherever a reviewer is
 * scoring an ATTEMPT. The live row is unique per candidate and its
 * sections 07-09 are cleared when they begin another sitting, so the
 * live row answers "what do they say today" while this answers "what
 * did they say when they sat the exam you are looking at" -- different
 * questions the moment anybody sits twice.
 *
 * Falls back server-side to the live row for attempts taken before
 * snapshots existed.
 */
export function useAttemptApplication(
  attemptId: string | null,
): UseQueryResult<CandidateApplication> {
  return useQuery({
    queryKey: ["application", "attempt", attemptId ?? ""],
    enabled: !!attemptId,
    retry: false, // a 404 here is an answer, not a failure to retry
    queryFn: async () => {
      const { data } = await apiClient.get<CandidateApplication>(
        `/tests/attempts/${attemptId}/application`,
      );
      return data;
    },
  });
}

/**
 * What the candidate said at their PREVIOUS sitting.
 *
 * For comparison only, and 404 when there is no earlier one — which is
 * the ordinary case, since most candidates sit once. `retry: false`
 * because that 404 is an answer, not a failure to retry.
 */
export function usePreviousAttemptApplication(
  attemptId: string | null,
): UseQueryResult<CandidateApplication> {
  return useQuery({
    queryKey: ["application", "attempt", attemptId ?? "", "previous"],
    enabled: !!attemptId,
    retry: false,
    queryFn: async () => {
      const { data } = await apiClient.get<CandidateApplication>(
        `/tests/attempts/${attemptId}/application/previous`,
      );
      return data;
    },
  });
}
